// src/components/FloatingTimer.js
//
// The tracking pill, plus a "pop out" that puts the timer in a small window
// that stays on top of every app and screen (Chrome / Edge 116+, via the
// Document Picture-in-Picture API). The pop-out shows the running time and
// project, and can clock out. Other browsers just keep the in-page pill.
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { NavLink } from "react-router-dom";
import { PictureInPicture2 } from "lucide-react";
import { useTimeTracking } from "../context/TimeTrackingContext";

const fmt = s => [Math.floor(s / 3600), Math.floor((s % 3600) / 60), s % 60].map(n => String(n).padStart(2, "0")).join(":");
export const canPopOut = () => typeof window !== "undefined" && "documentPictureInPicture" in window;

function MiniTimer({ elapsed, project, onClockOut, busy }) {
  return (
    <div style={{
      height: "100vh", boxSizing: "border-box", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
      padding: "10px 14px", background: "#0f9d6b", color: "#fff", fontFamily: "Inter, system-ui, -apple-system, Segoe UI, sans-serif",
    }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 22, fontWeight: 800, fontVariantNumeric: "tabular-nums", letterSpacing: ".01em" }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#fff", flexShrink: 0, animation: "tbpulse 2s ease infinite" }} />
          {fmt(elapsed)}
        </div>
        {project && <div style={{ fontSize: 12, opacity: .85, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{project}</div>}
      </div>
      <button
        onClick={onClockOut} disabled={busy}
        style={{ flexShrink: 0, border: "1px solid rgba(255,255,255,.55)", background: "rgba(255,255,255,.14)", color: "#fff", borderRadius: 8, padding: "7px 10px", fontSize: 12, fontWeight: 700, cursor: busy ? "default" : "pointer", fontFamily: "inherit" }}
      >
        {busy ? "…" : "Clock out"}
      </button>
    </div>
  );
}

export default function FloatingTimer() {
  const tt = useTimeTracking();
  const [pip, setPip] = useState(null);
  const pipRef = useRef(null);

  // Close the pop-out when tracking stops.
  useEffect(() => {
    if (!tt?.tracking && pipRef.current) { pipRef.current.close(); pipRef.current = null; setPip(null); }
  }, [tt?.tracking]);
  useEffect(() => () => { pipRef.current?.close(); }, []);

  async function popOut(e) {
    e.preventDefault(); e.stopPropagation();
    if (pipRef.current) { pipRef.current.focus?.(); return; }
    try {
      const win = await window.documentPictureInPicture.requestWindow({ width: 260, height: 78 });
      win.document.title = "Tech Bugz timer";
      const style = win.document.createElement("style");
      style.textContent = "html,body{margin:0;padding:0;overflow:hidden}@keyframes tbpulse{0%,100%{opacity:1}50%{opacity:.35}}";
      win.document.head.appendChild(style);
      win.addEventListener("pagehide", () => { pipRef.current = null; setPip(null); });
      pipRef.current = win; setPip(win);
    } catch (err) {
      console.error("Couldn't open the floating timer:", err.message);
    }
  }

  if (!tt?.tracking) return null;
  const project = tt.active?.projectName || "";
  // Asked inside the pop-out itself — the app tab is usually hidden behind other windows.
  const clockOut = () => { if ((pipRef.current || window).confirm("Clock out now?")) tt.clockOut(); };

  return (
    <>
      <div style={{ position: "fixed", bottom: 20, right: 20, zIndex: 800, display: "flex", alignItems: "center", gap: 6 }}>
        <NavLink
          to="/admin/time"
          title="Tracking in progress — click to open the tracker"
          style={{
            display: "flex", alignItems: "center", gap: 9,
            padding: "10px 16px", borderRadius: 99, textDecoration: "none",
            background: "var(--green)", color: "#fff",
            boxShadow: "0 8px 24px rgba(16,185,129,.35)",
            fontSize: 13, fontWeight: 700, fontVariantNumeric: "tabular-nums",
          }}
        >
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#fff", animation: "pulse 2s ease infinite" }} />
          {fmt(tt.elapsed)}
        </NavLink>
        {canPopOut() && !pip && (
          <button
            onClick={popOut}
            title="Keep the timer on top of every app and screen"
            aria-label="Pop out timer"
            style={{ width: 38, height: 38, borderRadius: 99, border: "none", background: "var(--green)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 8px 24px rgba(16,185,129,.35)" }}
          >
            <PictureInPicture2 size={16} />
          </button>
        )}
      </div>
      {pip && createPortal(<MiniTimer elapsed={tt.elapsed} project={project} onClockOut={clockOut} busy={tt.busy} />, pip.document.body)}
    </>
  );
}
